import { z } from "zod";
import { PersistableViewSchema } from "@common/types/views/views";
import { TaskSortValue } from "@common/types/views/task-view-enums";
import { MessageType } from "@common/types/chat";
import { zQueryBoolean } from "@common/schemas/query-boolean.schema";
import { TaskStatus, TaskPriority, TaskType, TaskGroupBy } from "../types/task";

export const MAX_PER_TRANSACTION_TASK_CREATION = 50;

const TaskRelationIdsShape = {
    blockedByTaskIds: z.array(z.string().uuid()).optional(),
    blockingTaskIds: z.array(z.string().uuid()).optional(),
    relatedTaskIds: z.array(z.string().uuid()).optional(),
};

export const TaskCreateSchema = z
    .object({
        title: z.string().min(1, { message: "Required" }),
        description: z.string().optional().nullable(),
        descriptionType: z.nativeEnum(MessageType).optional(),
        assigneeId: z.number().optional().nullable(),
        status: z.nativeEnum(TaskStatus).optional(),
        priority: z.nativeEnum(TaskPriority).optional().nullable(),
        dueDate: z.string().date().optional().nullable(),
        estimatedTime: z.number().optional().nullable(),
        entityIds: z.array(z.number()).optional(),
        groupId: z.string().uuid().optional().nullable(),
        closeStageId: z.string().uuid().optional().nullable(),
        closeId: z.string().uuid().optional().nullable(),
        messageId: z.string().uuid().optional().nullable(),
        type: z.nativeEnum(TaskType),
        view: PersistableViewSchema.optional(),
        financialDocumentIds: z
            .array(z.number())
            .max(500)
            .optional()
            .transform((ids) => (ids ? [...new Set(ids)] : ids)),
        createTaskPerTransaction: z.boolean().optional(),
        sticky: z.boolean().optional(),
        ...TaskRelationIdsShape,
    })
    .superRefine((val, ctx) => {
        if (val.createTaskPerTransaction !== true) {
            return;
        }

        const transactionIds = val.view?.filters?.transactionIds ?? [];
        if (transactionIds.length > MAX_PER_TRANSACTION_TASK_CREATION) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["view", "filters", "transactionIds"],
                message: `Cannot create more than ${MAX_PER_TRANSACTION_TASK_CREATION} tasks at once`,
            });
        }

        const hasRelations = [
            val.blockedByTaskIds,
            val.blockingTaskIds,
            val.relatedTaskIds,
        ].some((ids) => (ids?.length ?? 0) > 0);
        if (transactionIds.length > 1 && hasRelations) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["createTaskPerTransaction"],
                message:
                    "Task relations can't be set when creating a task per transaction",
            });
        }
    });
export type TaskCreate = z.infer<typeof TaskCreateSchema>;

export const TaskUpdateSchema = z.object({
    title: z.string().min(1).optional(),
    assigneeId: z.number().optional().nullable(),
    status: z.nativeEnum(TaskStatus).optional(),
    priority: z.nativeEnum(TaskPriority).optional().nullable(),
    dueDate: z.string().date().optional().nullable(),
    closeId: z.string().uuid().optional().nullable(),
    closeStageId: z.string().uuid().optional().nullable(),
    entityIds: z.array(z.number()).optional(),
    groupId: z.string().uuid().optional().nullable(),
    estimatedTime: z.number().optional().nullable(),
    financialDocumentIds: z
        .array(z.number())
        .max(500)
        .optional()
        .describe(
            "Deprecated: use POST .../documents/link and .../documents/unlink",
        )
        .transform((ids) => (ids ? [...new Set(ids)] : ids)),
    ...TaskRelationIdsShape,
});
export type TaskUpdate = z.infer<typeof TaskUpdateSchema>;

export const TaskListQuerySchema = z.object({
    assigneeIds: z.array(z.coerce.number()).optional(),
    isUnassigned: zQueryBoolean().optional(),
    isAssignedToClient: zQueryBoolean().optional(),
    createdByIds: z.array(z.coerce.number()).optional(),
    isCreatedByClient: zQueryBoolean().optional(),
    clientIds: z.array(z.string().uuid()).optional(),
    createdAtStart: z.string().date().optional(),
    createdAtEnd: z.string().date().optional(),
    completedByIds: z.array(z.coerce.number()).optional(),
    completedAtStart: z.string().date().optional(),
    completedAtEnd: z.string().date().optional(),
    closeIds: z.array(z.string().uuid()).optional(),
    ids: z.array(z.string().uuid()).optional(),
    status: z.array(z.nativeEnum(TaskStatus)).optional(),
    groupBy: z.nativeEnum(TaskGroupBy).optional(),
    entityIds: z.array(z.coerce.number()).optional(),
    start: z.coerce.date().optional(),
    end: z.coerce.date().optional(),
    search: z.string().optional(),
    sort: z.nativeEnum(TaskSortValue).optional(),
    priorities: z.array(z.nativeEnum(TaskPriority)).optional(),
    hasAttachments: zQueryBoolean().optional(),
    taskGroupIds: z.array(z.string().uuid()).optional(),
    hasBlockedBy: zQueryBoolean().optional(),
    hasBlocking: zQueryBoolean().optional(),
    hasRelated: zQueryBoolean().optional(),
    hasUnreadChat: zQueryBoolean().optional(),
    taskCloseStageIds: z.array(z.string().uuid()).optional(),
    includeRelationIds: zQueryBoolean().optional(),
});
export type TaskListQuery = z.infer<typeof TaskListQuerySchema>;

export const TaskBulkUpdateStatusActionSchema = z.object({
    type: z.literal("status"),
    status: z.nativeEnum(TaskStatus),
});

export const TaskBulkUpdateAssigneeActionSchema = z.object({
    type: z.literal("assignee"),
    assigneeId: z.number().nullable(),
});

export const TaskBulkUpdatePriorityActionSchema = z.object({
    type: z.literal("priority"),
    priority: z.nativeEnum(TaskPriority).nullable(),
});

export const TaskBulkUpdateTaskGroupActionSchema = z.object({
    type: z.literal("taskGroup"),
    groupId: z.string().uuid().nullable(),
});

export const TaskBulkUpdateEntityOperationSchema = z.object({
    entityId: z.number(),
    /** "add" inserts the entity on tasks that don't have it; "remove" deletes it from all selected tasks */
    operation: z.enum(["add", "remove"]),
});
export type TaskBulkUpdateEntityOperation = z.infer<
    typeof TaskBulkUpdateEntityOperationSchema
>;

export const TaskBulkUpdateEntityActionSchema = z.object({
    type: z.literal("entity"),
    /**
     * The staged add/remove edits are applied as one atomic batch so a
     * mid-sequence failure can't leave tasks partially updated.
     */
    operations: z.array(TaskBulkUpdateEntityOperationSchema).min(1),
});

export const TaskBulkUpdateActionSchema = z.discriminatedUnion("type", [
    TaskBulkUpdateStatusActionSchema,
    TaskBulkUpdateAssigneeActionSchema,
    TaskBulkUpdatePriorityActionSchema,
    TaskBulkUpdateTaskGroupActionSchema,
    TaskBulkUpdateEntityActionSchema,
]);
export type TaskBulkUpdateAction = z.infer<typeof TaskBulkUpdateActionSchema>;

/** Largest bulk update the server applies before answering; bigger ones run as a job. */
export const TASK_BULK_UPDATE_SYNC_THRESHOLD = 10;

export const TaskBulkUpdateSchema = z.object({
    taskIds: z.array(z.string().uuid()).min(1).max(500),
    action: TaskBulkUpdateActionSchema,
});
export type TaskBulkUpdate = z.infer<typeof TaskBulkUpdateSchema>;

export const TaskThreadUnreadCountSchema = z.object({
    taskId: z.string().uuid(),
    unreadCount: z.number().int(),
});
export type TaskThreadUnreadCount = z.infer<typeof TaskThreadUnreadCountSchema>;

export const StickyRefreshResponseSchema = z.object({
    addedCount: z.number().int(),
    transactionIds: z.array(z.number().int()).optional(),
    counterpartiesIds: z.array(z.string().uuid()).optional(),
    billIds: z.array(z.string().uuid()).optional(),
    invoiceIds: z.array(z.string().uuid()).optional(),
    financialDocumentIds: z.array(z.number().int()).optional(),
});
export type StickyRefreshResponse = z.infer<typeof StickyRefreshResponseSchema>;

export const TaskDocumentPathParamsSchema = z.object({
    workspaceId: z.string().uuid(),
    taskId: z.string().uuid(),
});

export const TaskDocumentBodySchema = z.object({
    financialDocumentIds: z
        .array(z.number().int())
        .min(1)
        .max(500)
        .transform((ids) => [...new Set(ids)]),
});

export const TaskDocumentLinkResponseSchema = z.object({
    data: z.object({
        financialDocumentIds: z.array(z.number().int()),
    }),
});
